import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["segment", "scrollContainer"]
  // Opt-in for transcript layouts that should follow playback. Other review,
  // audio-version, and inline transcripts leave it false so their
  // highlight-only behaviour is unchanged.
  static values = { autoScroll: Boolean }

  connect() {
    this.activeSegmentIndex = -1
    this.mediaElement = this.findMediaElement()
    this.buildSegmentTimes()
    this.setupMediaEvents()
    this.highlightActiveSegment(this.currentSecond)
  }

  disconnect() {
    this.teardownMediaEvents()
    this.clearActiveSegment()
  }

  findMediaElement() {
    return this.element.querySelector('[data-media-player-target="media"]') || this.element.querySelector("audio")
  }

  setupMediaEvents() {
    if (!this.mediaElement) return

    this.boundTimeUpdate = this.handlePlaybackPositionChange.bind(this)
    this.boundSeeked = this.handlePlaybackPositionChange.bind(this)
    this.boundMetadataLoaded = this.handlePlaybackPositionChange.bind(this)
    this.boundEnded = this.handleEnded.bind(this)

    this.mediaElement.addEventListener("timeupdate", this.boundTimeUpdate)
    this.mediaElement.addEventListener("seeked", this.boundSeeked)
    this.mediaElement.addEventListener("loadedmetadata", this.boundMetadataLoaded)
    this.mediaElement.addEventListener("ended", this.boundEnded)
  }

  teardownMediaEvents() {
    if (!this.mediaElement) return

    this.mediaElement.removeEventListener("timeupdate", this.boundTimeUpdate)
    this.mediaElement.removeEventListener("seeked", this.boundSeeked)
    this.mediaElement.removeEventListener("loadedmetadata", this.boundMetadataLoaded)
    this.mediaElement.removeEventListener("ended", this.boundEnded)
  }

  buildSegmentTimes() {
    if (!this.hasSegmentTarget) {
      this.segmentStarts = []
      return
    }

    this.segmentStarts = this.segmentTargets.map((segment) => {
      const startSecond = Number.parseFloat(segment.dataset.startSecond)
      return Number.isFinite(startSecond) ? startSecond : 0
    })
  }

  seekTo(event) {
    // Space on a focused segment would otherwise scroll the page; a no-op for
    // the click and Enter paths.
    event.preventDefault()
    if (!this.mediaElement) return

    const segment = event.currentTarget
    const startSecond = Number.parseFloat(segment.dataset.startSecond)
    if (!Number.isFinite(startSecond)) return

    this.mediaElement.currentTime = startSecond
    this.highlightActiveSegment(startSecond)

    const playPromise = this.mediaElement.play()
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {})
    }
  }

  handlePlaybackPositionChange() {
    this.highlightActiveSegment(this.currentSecond)
  }

  handleEnded() {
    this.clearActiveSegment()
  }

  get currentSecond() {
    if (!this.mediaElement) return 0

    const second = this.mediaElement.currentTime
    return Number.isFinite(second) ? second : 0
  }

  highlightActiveSegment(currentTime) {
    if (!this.segmentStarts || this.segmentStarts.length === 0) {
      this.clearActiveSegment()
      return
    }

    let activeIndex = -1
    for (let i = this.segmentStarts.length - 1; i >= 0; i--) {
      if (currentTime >= this.segmentStarts[i]) {
        activeIndex = i
        break
      }
    }

    if (activeIndex === this.activeSegmentIndex) return
    this.activeSegmentIndex = activeIndex

    this.segmentTargets.forEach((el, i) => {
      const isActive = i === activeIndex
      el.classList.toggle("active", isActive)
      if (isActive) {
        el.setAttribute("aria-current", "true")
      } else {
        el.removeAttribute("aria-current")
      }
    })

    this.keepActiveSegmentInView(activeIndex)
  }

  // Keep-in-view scroll of the active line, opt-in via autoScrollValue. When a
  // scroll container is supplied, move only that element and keep the page
  // still. Existing consumers without one retain scrollIntoView behavior.
  keepActiveSegmentInView(activeIndex) {
    if (!this.autoScrollValue || activeIndex < 0) return

    const behavior = this.prefersReducedMotion ? "auto" : "smooth"
    const segment = this.segmentTargets[activeIndex]

    if (this.hasScrollContainerTarget && this.scrollContainerTarget.clientHeight > 0) {
      const container = this.scrollContainerTarget
      const containerRect = container.getBoundingClientRect()
      const segmentRect = segment.getBoundingClientRect()
      const top = container.scrollTop +
        segmentRect.top -
        containerRect.top -
        (container.clientHeight - segmentRect.height) / 2

      container.scrollTo({ top: Math.max(0, top), behavior })
      return
    }

    segment.scrollIntoView({ block: "nearest", behavior })
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  clearActiveSegment() {
    this.activeSegmentIndex = -1
    this.segmentTargets.forEach((el) => {
      el.classList.remove("active")
      el.removeAttribute("aria-current")
    })
  }
}
